import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Modal, Select, Space, Tag, Tooltip, Typography } from "antd";
import { useDeferredValue, useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";

export type PlatformSKU = Schemas["PlatformSKU"];

/** Everything that shows mappings or stock depends on a mapping change. */
export function useRefreshMapping() {
  const queryClient = useQueryClient();
  return () => {
    for (const key of ["platform-skus", "duplicates", "skus", "prices", "inventory", "orders"]) void queryClient.invalidateQueries({ queryKey: [key] });
  };
}

/** Internal SKU picker with server-side search (code, name or barcode). */
export function SkuSelect({ value, onChange, autoFocus, singleOnly, placeholder }: {
  value?: string;
  onChange?: (id: string) => void;
  autoFocus?: boolean;
  /** Leave bundles out (they hold no stock: purchase orders, prices). */
  singleOnly?: boolean;
  placeholder?: string;
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState("");
  const q = useDeferredValue(search.trim());
  const list = useQuery({
    queryKey: ["skus", "pick", q, singleOnly],
    queryFn: () => unwrap(api.GET("/v1/skus", { params: { query: { status: "active", limit: 30, ...(singleOnly ? { kind: "single" as const } : {}), ...(q ? { q } : {}) } } })),
  });
  return (
    <Select<string>
      showSearch={{ filterOption: false, onSearch: setSearch }}
      autoFocus={autoFocus}
      value={value}
      onChange={onChange}
      loading={list.isFetching}
      placeholder={placeholder ?? t("catalog.skuPickPlaceholder")}
      style={{ width: "100%" }}
      options={(list.data?.items ?? []).map((s) => ({
        value: s.id,
        label: (
          <span>
            <span className="z-num">{s.code}</span> · {s.name}
          </span>
        ),
      }))}
    />
  );
}

/** "Petakan": the chosen platform SKUs to one internal SKU. */
export function MapModal({ ids, onClose }: { ids: string[] | null; onClose: (done: boolean) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefreshMapping();
  const [skuId, setSkuId] = useState<string>();
  const map = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/catalog/platform-skus/map", { body: { ids: ids!, skuId: skuId! } })),
    onSuccess: () => {
      void message.success(t("catalog.mapped"));
      refresh();
      setSkuId(undefined);
      onClose(true);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open={ids !== null && ids.length > 0}
      title={t("catalog.mapTitle", { n: ids?.length ?? 0 })}
      okText={t("catalog.map")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: !skuId }}
      confirmLoading={map.isPending}
      onOk={() => map.mutate()}
      onCancel={() => onClose(false)}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t("catalog.mapBody")}</Typography.Paragraph>
      <Typography.Text strong>{t("catalog.skuPick")}</Typography.Text>
      <div style={{ marginTop: 8 }}>
        <SkuSelect value={skuId} onChange={setSkuId} autoFocus />
      </div>
    </Modal>
  );
}

/** Bulk actions on platform SKUs that need no extra input. */
export function usePlatformActions() {
  const { t } = useI18n();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefreshMapping();

  const createFromPlatform = useMutation({
    mutationFn: (ids: string[]) => unwrap(api.POST("/v1/catalog/platform-skus/create-skus", { body: { ids } })),
    onSuccess: ({ results }) => {
      const ok = results.filter((r) => r.ok);
      const failed = results.length - ok.length;
      const created = ok.filter((r) => r.created).length;
      void message.success(t("catalog.createdFromPlatform", { created, reused: ok.length - created }));
      if (failed > 0) void message.warning(t("catalog.createdPartial", { ok: ok.length, failed }));
      refresh();
    },
    onError: (err) => void message.error(errorText(err)),
  });

  const untrack = (ids: string[], title: string, after?: () => void) =>
    modal.confirm({
      title,
      content: t("catalog.untrackBody"),
      okText: t("catalog.unmap"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: async () => {
        try {
          await unwrap(api.POST("/v1/catalog/platform-skus/unmap", { body: { ids } }));
          void message.success(t("catalog.untracked"));
          refresh();
          after?.();
        } catch (err) {
          void message.error(errorText(err));
        }
      },
    });

  return { createFromPlatform, untrack };
}

/** "Dipetakan ke": the internal SKU with how it got mapped, or why there is none. */
export function MappedTo({ p }: { p: PlatformSKU }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  if (p.mappingSource === "unmapped") {
    return (
      <Tooltip title={t("catalog.mapping.untrackedHelp")}>
        <Tag>{t("catalog.mapping.untracked")}</Tag>
      </Tooltip>
    );
  }
  if (!p.mappedTo) return <Typography.Text type="secondary">—</Typography.Text>;
  return (
    <Space orientation="vertical" size={0}>
      <Typography.Link className="z-num" onClick={() => navigate(`/app/catalog/${p.mappedTo!.id}`)}>
        {p.mappedTo.code}
      </Typography.Link>
      <Space size={4}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {p.mappedTo.name}
        </Typography.Text>
        {p.mappingSource && <Tag style={{ fontSize: 11 }}>{t(`catalog.mapping.${p.mappingSource === "auto_code" ? "auto_code" : "manual"}`)}</Tag>}
      </Space>
    </Space>
  );
}
